import { approveProposal } from 'cloudFunctions'
import { Loading, LoadingWrap } from 'components/Shared/Loading'
import { Heading, Text } from 'components/Ui-Kits/Text'
import { useAppTranslation } from 'i18n/hooks'
import { useEffect } from 'react'
import { useCallback } from 'react'
import { useRef } from 'react'
import { useState } from 'react'
import { useParams } from 'react-router'
import styled from 'styled-components'
import bg from 'assets/images/bottom-banner-bg.jpg'
import { debounce } from 'lodash'
import { Link } from 'react-router-dom'
import { Button } from 'components/Button'
import { WEBSHOP_BASE_URL } from 'constants/index'

// By the token type the server sends with the error: an offer for a
// reception, or the seller's items for an auction.
const ERROR_TEXTS = {
  reception: {
    'functions/deadline-exceeded': ['offerExpired', 'offerExpiredDescription'],
    'functions/already-exists': ['offerAlreadyAccepted', 'offerAlreadyAcceptedDescription'],
  },
  product: {
    'functions/deadline-exceeded': ['approvalLinkExpired', 'approvalLinkExpiredDescription'],
    'functions/already-exists': ['productsAlreadyApproved', 'productsAlreadyApprovedDescription'],
  },
}
const DEFAULT_ERROR_TEXTS = ['approvalLinkNotFound', 'approvalLinkNotFoundDescription']

// The admin form stores the proposed price as typed text or as a number.
// Format it when it is a number and show anything else as it is.
const formatAmount = amount => {
  const number = Number(amount)
  return amount !== '' && Number.isFinite(number) ? number.toLocaleString('fi-FI') : amount
}

export const Approve = () => {
  const ref = useRef(null)
  const { token } = useParams()
  const [minHeight, setMinHeight] = useState(0)
  const { t } = useAppTranslation()
  const calculateHeight = useCallback(() => {
    if (window.innerWidth < 760) return 0
    return window.innerHeight - ref.current.offsetTop
  }, [])
  // step: 'loading' | 'confirm' | 'submitting' | 'approved' | 'error'
  const [step, setStep] = useState('loading')
  const [proposal, setProposal] = useState(null)
  const [failure, setFailure] = useState(null)

  useEffect(() => {
    setMinHeight(calculateHeight())
    const callback = debounce(() => setMinHeight(calculateHeight()), 400)

    window.addEventListener('resize', callback)

    return () => {
      window.removeEventListener('resize', callback)
      callback.cancel()
    }
  }, [calculateHeight])

  useEffect(() => {
    // Same function as the approval below: this call starts its instance,
    // so pressing Accept does not wait for a cold start.
    approveProposal({ token, approve: false })
      .then(({ data }) => {
        setProposal(data)
        setStep('confirm')
      })
      .catch(error => {
        setFailure({ code: error?.code, type: error?.details?.type })
        setStep('error')
      })
  }, [token])

  const approve = () => {
    setStep('submitting')
    approveProposal({ token, approve: true })
      .then(() => setStep('approved'))
      .catch(error => {
        // When the first response is lost (seen on a function cold start) the
        // browser can resend the request, and the resent one finds the token
        // already used by the first. The server uses the token only together
        // with the approval, and a link scanner does not press the button, so
        // the approval is stored and is this seller's own.
        if (error?.code === 'functions/already-exists') return setStep('approved')
        setFailure({ code: error?.code, type: error?.details?.type })
        setStep('error')
      })
  }

  const frontPageButton = (
    <Button as="a" href={WEBSHOP_BASE_URL} color="primary">
      {t('goToFrontPage')}
    </Button>
  )
  const isReception = proposal?.type === 'reception'

  const renderContent = () => {
    switch (step) {
      case 'confirm':
      case 'submitting':
        return (
          <>
            <Heading color="primary" mb={0} mt={0}>
              {isReception ? t('offerHeading', { amount: formatAmount(proposal.amount) }) : t('approveProductsHeading')}
            </Heading>
            <StyledText color="primary">
              {isReception ? t('offerConfirmDescription') : t('approveProductsCount', { count: proposal.itemCount })}
            </StyledText>
            <Button color="primary" large disabled={step === 'submitting'} onClick={approve}>
              {step === 'submitting' ? t('accepting') : t('accept')}
            </Button>
          </>
        )
      case 'approved':
        return (
          <>
            <Heading color="primary" mb={0} mt={0}>
              {isReception ? t('youApprovedReception') : t('youApprovedProducts')}
            </Heading>
            <StyledText color="primary">
              {isReception ? t('weWillSendYouEmail') : t('youApprovedProductsDescription').trim()}
            </StyledText>
            {isReception ? (
              frontPageButton
            ) : (
              <Button as={Link} to="/profile" color="primary">
                {t('goToMyHolmasto')}
              </Button>
            )}
          </>
        )
      case 'error': {
        const texts = ERROR_TEXTS[failure?.type] || ERROR_TEXTS.reception
        const [heading, description] = texts[failure?.code] || DEFAULT_ERROR_TEXTS
        return (
          <>
            <Heading color="primary" mb={0} mt={0}>
              {t(heading)}
            </Heading>
            <StyledText color="primary">{t(description)}</StyledText>
            {frontPageButton}
          </>
        )
      }
      default:
        return (
          <LoadingWrap>
            <Loading />
          </LoadingWrap>
        )
    }
  }

  return (
    <Section minHeight={minHeight} ref={ref}>
      {renderContent()}
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: ${({ minHeight }) => minHeight}px;
  background-image: url(${bg});
  gap: 24px;
  padding: 24px;
`
const StyledText = styled(Text)`
  display: inline-block;
  text-align: center;
  white-space: pre-line;
  max-width: 600px;
`
